import AppConfig from '@app/libs/reactPdfJs/constants/AppConfig';
import {
  PDF_CHANGE_CURRENT_PAGE_NUMBER,
  PDF_CLEAR_ALL_STATE,
  PDF_LOAD_FAILURE,
  PDF_LOAD_RETRY,
  PDF_LOAD_START,
  PDF_LOAD_SUCCEED,
  PDF_NAVIGATE_TO_SPECIFIC_PAGE,
  PDF_PAGE_LEFT_POSITIONS_SET,
  PDF_PAGE_VIEWPORT_SET,
  PDF_SCALE_CHANGE,
  PDF_SCALE_NORMALIZE,
  PDF_VIEWER_PINCH_ZOOM_DISABLE,
  PDF_VIEWER_SCALE_CHANGE,
} from '@app/libs/reactPdfJs/store/actionTypes';
import PageViewport from '@app/libs/reactPdfJs/store/PageViewport';
import {
  maxScaleSelector,
  minScaleSelector,
  scaleSelector,
  viewerScaleSelector,
} from '@app/libs/reactPdfJs/store/selectors';
import { Thunk } from '@app/store/types';

export function pdfClearAllState() {
  return {
    type: PDF_CLEAR_ALL_STATE,
  };
}

export function pdfCurrentPageNumber(payload: number) {
  return {
    type: PDF_CHANGE_CURRENT_PAGE_NUMBER,
    payload,
  };
}

export function pdfLoadFailure(payload: Error) {
  return {
    type: PDF_LOAD_FAILURE,
    payload,
    error: true,
  };
}

export function pdfLoadRetry() {
  return {
    type: PDF_LOAD_RETRY,
  };
}

export function pdfLoadStart() {
  return {
    type: PDF_LOAD_START,
  };
}

export function pdfLoadSucceed() {
  return {
    type: PDF_LOAD_SUCCEED,
  };
}

export function pdfNavigateToSpecificPage(payload: number) {
  return {
    type: PDF_NAVIGATE_TO_SPECIFIC_PAGE,
    payload,
  };
}

export function pdfScaleChange(payload: number) {
  return {
    type: PDF_SCALE_CHANGE,
    payload,
  };
}

export function pdfPageLeftPositionsSet(payload: number[]) {
  return {
    type: PDF_PAGE_LEFT_POSITIONS_SET,
    payload,
  };
}

export function pdfPageViewportSet(payload: PageViewport[]) {
  return {
    type: PDF_PAGE_VIEWPORT_SET,
    payload,
  };
}

export function pdfScaleNormalize(defaultScale: number, maxScale: number, minScale: number) {
  return {
    type: PDF_SCALE_NORMALIZE,
    payload: { defaultScale, maxScale, minScale },
  };
}

export function pdfViewerPinchDisabled(payload: boolean) {
  return {
    type: PDF_VIEWER_PINCH_ZOOM_DISABLE,
    payload,
  };
}

export function pdfViewerScaleChange(payload: number) {
  return {
    type: PDF_VIEWER_SCALE_CHANGE,
    payload,
  };
}

export type PdfAction =
  | ReturnType<typeof pdfClearAllState>
  | ReturnType<typeof pdfCurrentPageNumber>
  | ReturnType<typeof pdfLoadFailure>
  | ReturnType<typeof pdfLoadRetry>
  | ReturnType<typeof pdfLoadStart>
  | ReturnType<typeof pdfLoadSucceed>
  | ReturnType<typeof pdfNavigateToSpecificPage>
  | ReturnType<typeof pdfPageLeftPositionsSet>
  | ReturnType<typeof pdfPageViewportSet>
  | ReturnType<typeof pdfScaleChange>
  | ReturnType<typeof pdfScaleNormalize>
  | ReturnType<typeof pdfViewerPinchDisabled>
  | ReturnType<typeof pdfViewerScaleChange>;

export const changeScale =
  (scale: number): Thunk =>
  (dispatch, getState) => {
    const currentScale = scaleSelector(getState());

    if (currentScale !== scale) {
      dispatch(pdfScaleChange(scale));
    }
  };

/**
 * window event에 의해 스케일을 조정한다.
 * PDF 컴포넌트 내부에서 문서 크기를 조정완료 후 실제 PDF_SCALE_CHANGE 액션이
 * 발생하도록 하기 위해 windowScale을 생성해서 처리한다.
 *
 * @param {number} scale - 확대, 축소에 대한 스케일
 */
export const changeWindowScale =
  (scale: number): Thunk =>
  (dispatch, getState) => {
    const currentWindowScale = viewerScaleSelector(getState());

    if (currentWindowScale !== scale) {
      dispatch(pdfViewerScaleChange(scale));
    }
  };

/**
 * 특정 스케일이 아닌 현재 스케일을 기준으로 확대를 진행한다.
 * @param {?number} ticksValue - 전체화면시 스크롤을 통한 확대, 축소시 사용되는 tick
 */
export const zoomIn =
  (ticksValue?: number): Thunk =>
  (dispatch, getState) => {
    const currentScale = scaleSelector(getState());
    const maxScale = maxScaleSelector(getState());
    let newScale = currentScale;
    let ticks = ticksValue ?? 0;

    do {
      newScale = Number((newScale * AppConfig.scale.defaultScaleDelta).toFixed(2));
      newScale = Math.ceil(newScale * 10) / 10;
      newScale = Math.min(maxScale, newScale);
      ticks -= 1;
    } while (ticks > 0 && newScale < maxScale);

    if (currentScale !== newScale) {
      dispatch(pdfViewerScaleChange(newScale));
    }
  };

/**
 * 특정 스케일이 아닌 현재 스케일을 기준으로 축소를 진행한다.
 * @param {?number} ticksValue - 전체화면시 스크롤을 통한 확대, 축소시 사용되는 tick
 */
export const zoomOut =
  (ticksValue?: number): Thunk =>
  (dispatch, getState) => {
    const currentScale = scaleSelector(getState());
    const minScale = minScaleSelector(getState());
    let newScale = currentScale;
    let ticks = ticksValue ?? 0;

    do {
      newScale = Number((newScale / AppConfig.scale.defaultScaleDelta).toFixed(2));
      newScale = Math.floor(newScale * 10) / 10;
      newScale = Math.max(minScale, newScale);
      ticks -= 1;
    } while (ticks > 0 && newScale > minScale);

    if (currentScale !== newScale) {
      dispatch(pdfViewerScaleChange(newScale));
    }
  };
